import React, { useState, useRef } from 'react';
import { Routes, Route, Link, useNavigate, Navigate } from 'react-router-dom';
import Animals from './pages/Animals';
import Beds from './pages/Beds';
import Food from './pages/Food';
import Products from './pages/Products';
import Recommendations from './pages/Recommendations';
import BestSeeds from './pages/BestSeeds';
import Inventory from './pages/Inventory';
import pkg from '../package.json';
import { InventoryProvider } from './pages/Inventory.jsx';
import Seeds from './pages/Seeds';
import { 
  Box, 
  Container, 
  Typography, 
  createTheme, 
  ThemeProvider, 
  CssBaseline,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import {
  LocalFlorist as LocalFloristIcon,
  Grass as GrassIcon,
  Park as ParkIcon,
  Restaurant as RestaurantIcon,
  ShoppingCart as ShoppingCartIcon,
  Save as SaveIcon,
  Upload as UploadIcon,
  ThumbUp as ThumbUpIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material';

// Import VersionStamp component
import VersionStamp from './components/VersionStamp';

// Test component removed to fix build

// Footer Component
const Footer = () => (
  <Box 
    component="footer" 
    sx={{ 
      width: '100%',
      py: 2,
      px: 2,
      backgroundColor: 'primary.dark',
      color: 'primary.contrastText',
      borderTop: '4px solid',
      borderColor: 'secondary.dark',
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: (theme) => theme.zIndex.drawer - 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    }}
  >
    <Box sx={{ ml: 2, display: 'flex', alignItems: 'center' }}>
      <VersionStamp />
    </Box>
    <Container maxWidth="md" sx={{ textAlign: 'center', flex: 1 }}>
      <Typography variant="body2" sx={{ color: '#f5f5dc', fontWeight: 500, mb: 1 }}>
        2025 Chainers.io Farm Community Wiki
      </Typography>
      <Typography variant="caption" sx={{ color: '#d4d4b0', display: 'block', mb: 0.5 }}>
        This is an unofficial community project and is not affiliated with or endorsed by Chainers.io.
      </Typography>
      <Typography variant="caption" sx={{ color: '#d4d4b0' }}>
        Game images and assets © Chainers.io. Used under fair use for informational purposes.
      </Typography>
    </Container>
    <Box sx={{ width: '200px' }} /> {/* Spacer to balance the layout */}
  </Box>
);

// Reusable Page Layout Component
const PageLayout = ({ children, title = '', noCard = false }) => {
  const maxWidth = '1200px';
  const footerHeight = '200px'; // Match this with the actual footer height
  const headerHeight = '64px';
  
  return (
    <Box sx={{ 
      minHeight: '100vh',
      width: '100%',
      backgroundColor: 'transparent',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      paddingBottom: footerHeight,
      overflow: 'hidden', // Prevent any horizontal scroll
    }}>
      <Box sx={{
        flex: '1 0 auto',  // Allow content to grow and take available space
        width: '100%',
        minHeight: `calc(100vh - ${headerHeight} - ${footerHeight})`,
        padding: '1rem 0 12rem', // Significantly increased bottom padding
        position: 'relative',
        zIndex: 1,
        boxSizing: 'border-box',
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        <Container 
          maxWidth={false} 
          disableGutters
          sx={{ 
            width: '100%', 
            maxWidth: maxWidth, 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            p: 0,
            m: 0,
            ...(noCard ? {} : {
              '& > .MuiBox-root': {
                width: '100%',
                p: 0,
                borderRadius: 0,
                boxShadow: 1,
                mb: 4,
                backgroundColor: 'transparent',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }
            })
          }}
        >
          {title && (
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom
              sx={{ 
                fontSize: { xs: '1.5rem', sm: '2rem' },
                textAlign: 'center',
                mb: { xs: 3, sm: 4 },
                fontWeight: 600,
                width: '100%'
              }}
            >
              {title}
            </Typography>
          )}
          {noCard ? (
            <Box sx={{ width: '100%' }}>
              {children}
            </Box>
          ) : (
            <Box sx={{ width: '100%' }}>
              {children}
            </Box>
          )}
        </Container>
      </Box>
      {/* Fixed footer */}
      <Box sx={{ 
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: footerHeight,
        backgroundColor: 'transparent',
        borderTop: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
        // Add a small gradient fade at the top of the footer
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-1.5rem',
          left: 0,
          right: 0,
          height: '1.5rem',
          background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.03))',
          pointerEvents: 'none'
        }
      }}>
        <Footer />
      </Box>
    </Box>
  );
};

// Pages
const Home = () => (
  <PageLayout noCard>
    <Box 
      component="main"
      sx={{ 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pt: 8, // Add top padding to create space below header
        pb: 4, // Keep some bottom padding
        px: 4,
        maxWidth: '800px',
        mx: 'auto',
        textAlign: 'center',
        minHeight: 'auto' // Remove fixed min-height to allow content to flow naturally
      }}
    >
      <Typography 
        variant="h4" 
        component="h1"
        sx={{ 
          fontWeight: 600,
          color: 'primary.dark',
          mb: 3
        }}
      >
        Welcome to the Chainers.io Farm Wiki
      </Typography>
      <Typography 
        variant="h6" 
        component="p"
        sx={{ 
          color: 'text.secondary',
          lineHeight: 1.7,
          mb: 1
        }}
      >
        This wiki provides information and tools to help you optimize your farming experience in Chainers.io.
      </Typography>
    </Box>
  </PageLayout>
);

const theme = createTheme({
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 500,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 500,
      lineHeight: 1.3,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 500,
      lineHeight: 1.3,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.3,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.3,
    },
    button: {
      textTransform: 'none',
    },
  },
  palette: {
    mode: 'light',
    primary: {
      main: '#2e5e3a', // Dark green
      light: '#4c8c5a',
      dark: '#1e3f27',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f5f5dc', // Sand yellow
      light: '#fffffe',
      dark: '#c2c2aa',
      contrastText: '#2e5e3a',
    },
    error: {
      main: '#f44336',
      light: '#e57373',
      dark: '#d32f2f',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
      contrastText: 'rgba(0, 0, 0, 0.87)',
    },
    info: {
      main: '#2196f3',
      light: '#64b5f6',
      dark: '#1976d2',
      contrastText: '#ffffff',
    },
    success: {
      main: '#4caf50',
      light: '#81c784',
      dark: '#388e3c',
      contrastText: 'rgba(0, 0, 0, 0.87)',
    },
    background: {
      default: '#f5f5dc', // Light sand yellow (beige)
      paper: '#ffffff', // White for cards and paper elements
    },
    text: {
      primary: '#1b5e20', // Dark green
      secondary: '#2e7d32',
      disabled: 'rgba(0, 0, 0, 0.38)',
    },
    divider: 'rgba(0, 0, 0, 0.08)',
    action: {
      active: '#4caf50', // Grass green
      hover: 'rgba(76, 175, 80, 0.08)', // Light green hover
      selected: 'rgba(76, 175, 80, 0.12)', // Slightly darker green for selected
    },
  },
  shape: {
    borderRadius: 4,
  },
  spacing: 8,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          padding: '8px 16px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 24px 0 rgba(0,0,0,0.1)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backdropFilter: 'blur(8px)',
          background: '#81c784', // Light grass green
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          color: '#1b5e20', // Dark green text
          '& .MuiButton-text': {
            color: 'inherit',
          },
        },
      },
    },
  },
});


const App = () => {
  // Apply the theme
  const currentTheme = theme;
  const fileInputRef = useRef();
  const navigate = useNavigate();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Global styles for html and body to prevent white bar
  React.useEffect(() => {
    document.documentElement.style.overflowX = 'hidden';
    document.documentElement.style.width = '100%';
    document.body.style.overflowX = 'hidden';
    document.body.style.width = '100%';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    
    return () => {
      document.documentElement.style.overflowX = '';
      document.documentElement.style.width = '';
      document.body.style.overflowX = '';
      document.body.style.width = '';
      document.body.style.margin = '';
      document.body.style.padding = '';
    };
  }, []);

  // Load inventory from JSON file
  const handleLoadInventory = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        let inventoryData;
        
        // Handle both old and new inventory formats
        if (data.inventory) {
          // New format: { inventory: { ... } }
          inventoryData = data.inventory;
        } else if (data.items && data.items.seeds) {
          // Old format: { items: { seeds: {...}, beds: {...}, ... } }
          inventoryData = data.items;
        } else {
          throw new Error('Unsupported inventory file format');
        }
        
        // Clean up any potential issues in the inventory data
        if (inventoryData.seeds) {
          // Remove any undefined or invalid entries
          Object.keys(inventoryData.seeds).forEach(key => {
            if (inventoryData.seeds[key] === '' || inventoryData.seeds[key] === null) {
              inventoryData.seeds[key] = 0;
            }
          });
        }
        
        localStorage.setItem('chainersInventory', JSON.stringify(inventoryData));
        window.dispatchEvent(new CustomEvent('inventoryUpdated', { detail: inventoryData }));
        navigate('/inventory');
        if (fileInputRef.current) fileInputRef.current.value = '';
        setSnackbar({ open: true, message: 'Inventory loaded successfully!', severity: 'success' });
      } catch (error) {
        console.error('Error loading inventory:', error);
        setSnackbar({ open: true, message: `Error loading inventory: ${error.message}`, severity: 'error' });
      }
    };
    reader.onerror = () => {
      setSnackbar({ open: true, message: 'Error reading file.', severity: 'error' });
    };
    reader.readAsText(file);
  };
  
  // Navigation component
  const Navigation = ({ fileInputRef }) => {

    // Save inventory as JSON file
    const handleSaveInventory = () => {
      try {
        const inventory = JSON.parse(localStorage.getItem('chainersInventory')) || {};
        
        // Clean the inventory data before exporting
        const cleanInventory = { ...inventory };
        
        // Clean seeds data
        if (cleanInventory.seeds) {
          cleanInventory.seeds = { ...cleanInventory.seeds };
          Object.keys(cleanInventory.seeds).forEach(key => {
            // Convert any empty strings or null values to 0
            if (cleanInventory.seeds[key] === '' || cleanInventory.seeds[key] === null) {
              cleanInventory.seeds[key] = 0;
            }
          });
        }
        
        // Create the export data with the new format
        const exportData = {
          inventory: cleanInventory,
          version: pkg.version,
          lastUpdated: new Date().toISOString(),
          metadata: {
            totalItems: Object.keys(cleanInventory.seeds || {}).length + 
                       Object.keys(cleanInventory.beds || {}).length + 
                       Object.keys(cleanInventory.animals || {}).length + 
                       Object.keys(cleanInventory.food || {}).length +
                       Object.keys(cleanInventory.products || {}).length
          }
        };
        
        const dataStr = JSON.stringify(exportData, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        
        const exportFileDefaultName = `chainers-inventory-${new Date().toISOString().split('T')[0]}.json`;
        
        const link = document.createElement('a');
        link.href = url;
        link.download = exportFileDefaultName;
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }, 100);
        
        setSnackbar({ open: true, message: 'Inventory exported successfully!', severity: 'success' });
      } catch (error) {
        console.error('Error exporting inventory:', error);
        setSnackbar({ open: true, message: `Error exporting inventory: ${error.message}`, severity: 'error' });
      }
    };

    const menuItems = [
      { text: 'Seeds', icon: <GrassIcon />, path: '/seeds' },
      { text: 'Beds', icon: <GrassIcon />, path: '/beds' },
      { text: 'Animals', icon: <ParkIcon />, path: '/animals' },
      { text: 'Food', icon: <RestaurantIcon />, path: '/food/birds' },
      { text: 'Products', icon: <ShoppingCartIcon />, path: '/products' },
      { text: 'Inventory', icon: <LocalFloristIcon />, path: '/inventory' },
      { text: 'Recommendations', icon: <ThumbUpIcon />, path: '/recommendations' },
      { text: 'Best Seeds', icon: <LocalFloristIcon />, path: '/best-seeds' },
      { text: 'Seed Notification', icon: <NotificationsIcon />, path: '/seed-notification' },
    ];

    return (
      <>
        <AppBar 
          position="fixed"
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderBottom: '4px solid',
            borderColor: 'secondary.dark'
          }}
          elevation={0}
        >
          <Toolbar>
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
              <LocalFloristIcon sx={{ display: { xs: 'none', md: 'flex' }, mr: 1 }} />
              <Typography
                variant="h6"
                noWrap
                component={Link}
                to="/"
                sx={{
                  mr: 2,
                  display: { xs: 'none', md: 'flex' },
                  fontWeight: 700,
                  color: 'inherit',
                  textDecoration: 'none',
                }}
              >
                Chainers.io Farm Wiki
              </Typography>
            </Box>

            <Box sx={{ flexGrow: 1, display: 'flex', ml: 3 }}>
              {menuItems.map((item) => (
                <Button
                  key={item.text}
                  component={Link}
                  to={item.path}
                  sx={{ 
                    my: 2, 
                    color: 'text.primary',
                    display: 'flex',
                    alignItems: 'center',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                  startIcon={item.icon}
                >
                  {item.text}
                </Button>
              ))}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title="Save Inventory">
                <IconButton 
                  color="inherit" 
                  onClick={handleSaveInventory}
                  sx={{ mr: 1 }}
                >
                  <SaveIcon />
                </IconButton>
              </Tooltip>
              
              <input
                type="file"
                accept=".json"
                onChange={handleLoadInventory}
                style={{ display: 'none' }}
                id="load-inventory-desktop"
                ref={fileInputRef}
              />
              <Tooltip title="Load Inventory">
                <label htmlFor="load-inventory-desktop">
                  <IconButton 
                    color="inherit"
                    component="span"
                    sx={{ mr: 1 }}
                  >
                    <UploadIcon />
                  </IconButton>
                </label>
              </Tooltip>
            </Box>
          </Toolbar>
        </AppBar>
      </>
    );
  };

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />
      <InventoryProvider>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', width: '100%', bgcolor: 'transparent' }}>
          <Navigation fileInputRef={fileInputRef} />
          
          <Box
            component="main"
            sx={{
              flex: '1 0 auto',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'transparent',
              pt: '80px',
              minHeight: 'calc(100vh - 80px)'
            }}
          >
            <Box
              sx={{
                flex: '1 0 auto',
                width: '100%',
                maxWidth: '1200px',
                mx: 'auto',
                overflowX: 'hidden',
                pb: 4 // Reduced padding to prevent content from being hidden behind fixed footer
              }}
            >
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/inventory" element={<Inventory />} />
                <Route path="/beds" element={<Beds />} />
                <Route path="/animals" element={<Animals />} />
                <Route path="/food" element={<Navigate to="/food/birds" replace />} />
                <Route path="/food/:animalType" element={<Food />} />
                <Route path="/products" element={<Products />} />
                <Route path="/recommendations" element={<Recommendations />} />
                <Route path="/best-seeds" element={<BestSeeds />} />
                <Route path="/seeds" element={<Seeds />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Box>
          </Box>
        </Box>
        
        <Footer />
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </InventoryProvider>
    </ThemeProvider>
  );
};

export default App;
